import React, { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useLocation } from "react-router-dom"
import {
    HiOutlineArrowLeftOnRectangle, HiOutlineHeart,
    HiOutlineUserCircle, HiOutlineSparkles, HiOutlineChatBubbleLeftRight, HiOutlineCreditCard,
} from "react-icons/hi2"
import { setLoggedIn, setUserDetails } from "../Store/Auth/actions"
import API from "../Common/API"
import { useLanguage } from "../Common/LanguageContext"
import NotificationPanel, { BellButton, useNotifications } from "./NotificationPanel"

const POLL_MS = 5000

const getPhoto = (u) => {
    const imgs = u?.images?.length ? u.images : u?.previewUrls || []
    return imgs.find(i => i?.imageUrl)?.imageUrl || null
}

const UnreadBadge = ({ count }) => {
    if (!count) return null
    return (
        <AnimatePresence>
            <motion.span
                key="badge"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                className="absolute -top-1 -right-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white shadow-sm"
            >
                {count > 99 ? "99+" : count}
            </motion.span>
        </AnimatePresence>
    )
}

const MemberTopBar = () => {
    const dispatch    = useDispatch()
    const history     = useHistory()
    const location    = useLocation()
    const userDetails = useSelector((state) => state.auth.userDetails)
    const { lang, setLang, t } = useLanguage()

    const [totalUnread, setTotalUnread] = useState(0)
    const [notifOpen, setNotifOpen] = useState(false)
    const intervalRef = useRef(null)
    const { notifications, unread: notifUnread, fetch: refreshNotifs, markRead, markAllRead } = useNotifications()

    const onChatPage = location.pathname.startsWith("/member/chat")

    /* ── poll conversations for unread count ── */
    const fetchUnread = async () => {
        try {
            const res = await API.get("/chat/conversations")
            const list = res.data?.data || []
            const sum  = list.reduce((s, c) => s + (c.unread || 0), 0)
            setTotalUnread(sum)
        } catch { /* silent */ }
    }

    useEffect(() => {
        fetchUnread()
        intervalRef.current = setInterval(fetchUnread, POLL_MS)
        return () => clearInterval(intervalRef.current)
    }, []) // eslint-disable-line

    useEffect(() => {
        if (onChatPage) setTotalUnread(0)
    }, [onChatPage])

    const memberName =
        userDetails?.name ||
        [userDetails?.firstName, userDetails?.lastName, userDetails?.surname].filter(Boolean).join(" ") ||
        userDetails?.userName ||
        "Member"

    const photo = getPhoto(userDetails)

    const logout = () => {
        clearInterval(intervalRef.current)
        localStorage.removeItem("token")
        localStorage.removeItem("accountType")
        localStorage.removeItem("userDetails")
        dispatch(setUserDetails({}))
        dispatch(setLoggedIn(false))
        window.location.href = "/member/login"
    }

    const NAV = [
        { label: t("navMatches"), path: "/member",         Icon: HiOutlineSparkles,            badge: 0                              },
        { label: t("navProfile"), path: "/member/profile", Icon: HiOutlineUserCircle,          badge: 0                              },
        { label: t("navChat"),    path: "/member/chat",    Icon: HiOutlineChatBubbleLeftRight, badge: onChatPage ? 0 : totalUnread   },
        { label: t("plansTab") || "Plans", path: "/member/plans", Icon: HiOutlineCreditCard,  badge: 0                              },
    ]

    return (
    <>
        <motion.header className="sticky top-0 z-30" style={{background:'linear-gradient(135deg,#0A1628,#112240)',borderBottom:'1px solid rgba(200,146,10,0.2)'}}>
            {/* Top row */}
            <div className="mx-auto flex max-w-[1380px] flex-wrap items-center justify-between gap-3 px-5 py-3 md:px-8">
                {/* User identity (Facebook-style) */}
                <div className="flex items-center gap-3">
                    {/* Profile photo 64×64 */}
                    <div style={{
                        width:64,height:64,borderRadius:'50%',overflow:'hidden',flexShrink:0,
                        border:'2.5px solid rgba(200,146,10,0.6)',
                        boxShadow:'0 0 0 4px rgba(200,146,10,0.14)',
                        background:'linear-gradient(135deg,#0A1628,#112240)',
                        display:'flex',alignItems:'center',justifyContent:'center',color:'#F5D264',
                    }}>
                        {photo
                            ? <img src={photo} alt={memberName} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                            : <HiOutlineUserCircle style={{fontSize:36}}/>
                        }
                    </div>
                    {/* Name + ref */}
                    <div>
                        <p style={{fontSize:17,fontWeight:800,color:'#F5D264',fontFamily:"'Playfair Display',serif",lineHeight:1.2,letterSpacing:'0.01em'}}>{memberName}</p>
                        <p style={{fontSize:11,fontWeight:600,color:'rgba(245,210,100,0.5)',letterSpacing:'0.05em',marginTop:2}}>{userDetails?.refId || userDetails?.mobileNumber || ""}</p>
                    </div>
                </div>

                {/* Right: lang toggle + avatar + logout */}
                <div className="flex items-center gap-3">
                    {/* EN / TA language toggle */}
                    <div style={{display:'flex',alignItems:'center',borderRadius:16,border:'1px solid rgba(200,146,10,0.25)',background:'rgba(255,255,255,0.06)',padding:3,gap:2}}>
                        {["en", "ta"].map((code) => (
                            <button
                                key={code}
                                type="button"
                                onClick={() => setLang(code)}
                                title={code === "en" ? "Switch to English" : "தமிழில் மாற்று"}
                                style={{
                                    borderRadius:12,padding:'5px 14px',fontSize:11,fontWeight:700,border:'none',cursor:'pointer',transition:'all .2s',
                                    background: lang===code ? 'linear-gradient(135deg,#C8920A,#D4A820)' : 'transparent',
                                    color: lang===code ? '#060E1A' : 'rgba(245,210,100,0.65)',
                                }}
                            >
                                {code === "en" ? "EN" : "த"}
                            </button>
                        ))}
                    </div>

                    {/* Bell notification */}
                    <BellButton unread={notifUnread} onRefresh={refreshNotifs} onClick={() => setNotifOpen(true)} />

                    <button type="button"
                        style={{display:'inline-flex',alignItems:'center',gap:6,minHeight:36,padding:'6px 16px',borderRadius:50,border:'1.5px solid rgba(200,146,10,0.35)',background:'rgba(200,146,10,0.1)',color:'#F5D264',fontSize:13,fontWeight:700,cursor:'pointer'}}
                        onClick={logout}>
                        <HiOutlineArrowLeftOnRectangle style={{fontSize:15}}/>
                        {t("logout")}
                    </button>
                </div>
            </div>

            {/* Nav tab row */}
            <div className="mx-auto flex max-w-[1380px] gap-1 px-5 md:px-8">
                {NAV.map(({ label, path, Icon, badge }) => {
                    const active = path === "/member"
                        ? (location.pathname === "/member" || location.pathname === "/member/matches")
                        : location.pathname === path || location.pathname.startsWith(path + "/")
                    return (
                        <button
                            key={path}
                            type="button"
                            onClick={() => history.push(path)}
                            style={{
                                position:'relative',display:'inline-flex',alignItems:'center',gap:7,
                                padding:'10px 18px',fontSize:13,fontWeight:700,border:'none',cursor:'pointer',
                                borderRadius:'12px 12px 0 0',transition:'all .2s',
                                background: active ? 'rgba(200,146,10,0.15)' : 'transparent',
                                color: active ? '#F5D264' : 'rgba(245,210,100,0.55)',
                                borderBottom: active ? '2px solid #C8920A' : '2px solid transparent',
                            }}
                        >
                            <span className="relative">
                                <Icon style={{width:16,height:16}}/>
                                <UnreadBadge count={badge} />
                            </span>
                            {label}
                            {badge > 0 && (
                                <motion.span
                                    animate={{ scale: [1, 1.25, 1] }}
                                    transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
                                    style={{marginLeft:2,display:'inline-block',width:7,height:7,borderRadius:'50%',background:'#ef4444'}}
                                    title={`${badge} unread`}
                                />
                            )}
                        </button>
                    )
                })}
            </div>
        </motion.header>

        <NotificationPanel
            open={notifOpen}
            onClose={() => setNotifOpen(false)}
            notifications={notifications}
            unread={notifUnread}
            markRead={markRead}
            markAllRead={markAllRead}
        />
    </>
    )
}

export default MemberTopBar
